import { useEffect, useRef, useState } from 'react';

import { LOADING_ANNOUNCE_DELAY_MS } from './useListStatusMessage';

export type LessonStatusResult = 'content' | 'empty' | 'error';

/** Textos ya traducidos; el que falte deja la región en silencio en ese momento. */
export type LessonStatusMessages = {
  empty?: string;
  error?: string;
  loaded?: string;
  loading?: string;
};

export type UseLessonStatusMessageParams = {
  isLoading: boolean;
  messages: LessonStatusMessages;
  result: LessonStatusResult;
};

/**
 * Mensaje de la región de estado de una vista con carga propia (WCAG 4.1.3): inicio, fin, vacío y
 * error. Nace vacío y muta después, porque una región que nace con su texto no se anuncia. El inicio
 * espera `LOADING_ANNOUNCE_DELAY_MS`, como en `useListStatusMessage` (TAN-10529).
 */
export default function useLessonStatusMessage({ isLoading, messages, result }: UseLessonStatusMessageParams): string {
  const { empty = '', error = '', loaded = '', loading = '' } = messages;

  const [message, setMessage] = useState<string>('');
  const hasStartedLoadingRef = useRef<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isLoading || !loading) return undefined;

    const timeoutId = setTimeout(() => {
      setMessage(loading);
    }, LOADING_ANNOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading, loading]);

  useEffect(() => {
    if (isLoading) {
      // Solo al entrar en carga: si cambian el resultado o los textos a mitad, no se borra el «Cargando» ya anunciado.
      if (wasLoadingRef.current) return;
      hasStartedLoadingRef.current = true;
      wasLoadingRef.current = true;
      // Se vacía en el acto aunque el inicio aún no se anuncie: si no, un «cargada» repetido no muta y no se oye.
      setMessage('');
      return;
    }

    if (!hasStartedLoadingRef.current) return;

    const resultMessages: Record<LessonStatusResult, string> = { content: loaded, empty, error };
    const hasJustLoaded = wasLoadingRef.current;
    wasLoadingRef.current = false;

    // Ya resuelta, el contenido que reaparece no se anuncia: solo el vacío o el error que surgen después.
    setMessage(hasJustLoaded || result !== 'content' ? resultMessages[result] : '');
  }, [empty, error, isLoading, loaded, result]);

  return message;
}
